import React, { useEffect, useMemo, useState } from 'react';
import { Field } from '../types';
import { FieldTypes, fieldType } from '../constants';
import InputWithToggleLabel from 'components/input/inputWithToggleLabel';
import { IOption } from 'components/input/selectInput';
import { isMultipleOfStep, roundValue } from '../utils';
export interface RobotSettingsInputProps {
  field: Field;
  value: any;
  onChange: (propertyName: string, value: any) => void;
  onToggle: (propertyName: string, value?: any) => void;
  formData: any;
  enabled: boolean;
  customClassName?: string;
  hasDuplicate?: boolean;
  placeholder?: string;
  defaultValue?: any;
  showToggle?: boolean;
}

function RobotSettingsInput(props: RobotSettingsInputProps) {
  const {
    field,
    value,
    onChange,
    onToggle,
    formData,
    enabled,
    customClassName,
    hasDuplicate,
    placeholder,
    defaultValue,
    showToggle,
  } = props;
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const {
    DisplayLabel,
    PropertyName,
    EnableRules,
    SingleOptionFieldProperty,
    NumericFieldProperty,
    FieldType,
    UnitLabel,
    IsMandatory,
    VisibleRules,
  } = field;

  const roundedMin = roundValue(NumericFieldProperty?.Min);
  const roundedMax = roundValue(NumericFieldProperty?.Max);

  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    let value;
    if (e.target.type === 'number') {
      value = parseFloat(e.target.value);
      const step = Number(NumericFieldProperty?.Step);
      if (value < roundedMin || value > roundedMax) {
        setFieldError(
          `${DisplayLabel} should be between ${roundedMin} and ${roundedMax}.`,
        );
      } else if (step && !isMultipleOfStep(value, step)) {
        setFieldError(`${DisplayLabel} should be a multiple of ${step}.`);
      } else {
        setFieldError(undefined);
      }
    } else {
      value = e.target.value;
    }
    onChange(PropertyName, value);
  };

  const shouldEnableField = useMemo(() => {
    if (!EnableRules || EnableRules.length === 0) {
      return true;
    }
    return EnableRules.every((rule) => {
      const ruleValue = formData?.[rule.PropertyName];
      if (rule.Reversed) {
        return ruleValue && ruleValue !== rule.Value;
      } else {
        return ruleValue === rule.Value;
      }
    });
  }, [EnableRules, formData]);

  const shouldDisplayField = useMemo(() => {
    if (!VisibleRules || VisibleRules.length === 0) {
      return true;
    }
    return VisibleRules.every((rule) => {
      const ruleValue = formData?.[rule.PropertyName];
      if (rule.Reversed) {
        return ruleValue && ruleValue !== rule.Value;
      } else {
        return ruleValue === rule.Value;
      }
    });
  }, [VisibleRules, formData]);

  const getDefaultFieldValue = () => {
    if (defaultValue) {
      return defaultValue;
    }
    switch (FieldType) {
      case FieldTypes.Numeric:
        return roundedMin.toString();
      case FieldTypes.SingleOption:
        return (
          SingleOptionFieldProperty?.Options?.[0].Value ||
          SingleOptionFieldProperty?.Options?.[0].Label
        );
      case FieldTypes.Boolean:
        return 'True';
      case FieldTypes.Range:
        return NumericFieldProperty?.Min.toString();
      default:
        return false;
    }
  };

  const toggleFieldEnabled = () => {
    if (shouldEnableField) {
      setFieldError(undefined);
      onToggle(PropertyName, getDefaultFieldValue());
    }
  };

  const fieldOptions: IOption[] = useMemo(() => {
    return SingleOptionFieldProperty?.Options?.map((o) => ({
      title: o.Label,
      value: o.Value,
    }));
  }, [field]);

  useEffect(() => {
    if (
      (!shouldEnableField && EnableRules.length && value && !hasDuplicate) ||
      (!shouldDisplayField && VisibleRules.length && value && !hasDuplicate) ||
      (!shouldEnableField && shouldDisplayField && value && hasDuplicate)
    ) {
      onChange(PropertyName, false);
    } else if (
      (shouldEnableField && EnableRules.length && !value) ||
      (shouldDisplayField && IsMandatory && !value)
    ) {
      onChange(PropertyName, getDefaultFieldValue());
    }
  }, [
    shouldEnableField,
    shouldDisplayField,
    IsMandatory,
    hasDuplicate,
    EnableRules,
    VisibleRules,
  ]);

  return shouldDisplayField ? (
    <div
      id="robotsettingsinput-wrapper"
      className={`flex flex-row justify-between items-center w-[45%] ${
        customClassName || ''
      }`}
    >
      <InputWithToggleLabel
        key={DisplayLabel}
        type={fieldType[FieldType]}
        value={value}
        label={DisplayLabel}
        handleChange={handleValueChange}
        toggle={showToggle ?? (!IsMandatory && !EnableRules.length)}
        unitLabel={UnitLabel}
        handleToggle={toggleFieldEnabled}
        min={roundedMin}
        max={roundedMax}
        step={NumericFieldProperty?.Step}
        options={fieldOptions}
        disabled={!EnableRules.length ? !enabled : !shouldEnableField}
        error={fieldError}
        placeholder={placeholder}
      />
    </div>
  ) : null;
}

export default RobotSettingsInput;
